import React from 'react';
import { Box } from '@mui/material';
import { motion } from 'framer-motion';
import ScrollReveal from './animated/ScrollReveal';
import Text from './Text';
import BrandName, { withBrand } from './BrandName';
import { colors, gradients } from '../theme/designTokens';

const SectionHeader = ({
  eyebrow,
  title,
  highlightWords = [],
  subtitle,
  align = 'center',
  light = false,
  animate = true,
}) => {
  const titleColor = light ? 'white' : 'primary';
  const subtitleColor = light ? 'muted' : 'secondary';
  const highlightColor = light ? colors.ochreLight : colors.navy;
  const eyebrowColor = light ? colors.ochre : colors.slate;

  const renderPart = (part, key) => {
    if (typeof part !== 'string') return part;
    if (/^ALEgION®?$/i.test(part)) {
      return <BrandName key={key} />;
    }
    return withBrand(part);
  };

  const renderTitle = () => {
    if (typeof title !== 'string') return title;
    if (!highlightWords.length) return withBrand(title);

    const parts = title.split(new RegExp(`(${highlightWords.join('|')})`, 'gi'));
    return parts.map((part, i) =>
      highlightWords.some((w) => w.toLowerCase() === part.toLowerCase()) ? (
        <Box component="span" key={i} sx={{ color: highlightColor }}>
          {renderPart(part, `h-${i}`)}
        </Box>
      ) : (
        <React.Fragment key={i}>{renderPart(part, `t-${i}`)}</React.Fragment>
      )
    );
  };

  const content = (
    <Box
      sx={{
        textAlign: align,
        mb: { xs: 3, md: 5 },
        maxWidth: align === 'center' ? 800 : 'none',
        mx: align === 'center' ? 'auto' : 0,
      }}
    >
      {eyebrow && (
        <Text
          variant="eyebrow"
          component={animate ? motion.p : 'p'}
          initial={animate ? { opacity: 0, y: 16 } : undefined}
          whileInView={animate ? { opacity: 1, y: 0 } : undefined}
          viewport={{ once: true }}
          transition={{ duration: 0.45 }}
          sx={{ color: eyebrowColor, mb: 1.5, display: 'block' }}
        >
          {eyebrow}
        </Text>
      )}
      <Text
        variant="h2"
        component={animate ? motion.h2 : 'h2'}
        color={titleColor}
        brand={false}
        initial={animate ? { opacity: 0, y: 24 } : undefined}
        whileInView={animate ? { opacity: 1, y: 0 } : undefined}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.05 }}
        sx={{ mb: subtitle ? 2 : 0, display: 'block' }}
      >
        {renderTitle()}
      </Text>
      <Box
        component={animate ? motion.div : 'div'}
        initial={animate ? { width: 0 } : undefined}
        whileInView={animate ? { width: 64 } : undefined}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.15 }}
        sx={{
          height: 4,
          borderRadius: 2,
          background: light ? gradients.ochre : gradients.navy,
          mx: align === 'center' ? 'auto' : 0,
          mb: subtitle ? 2.5 : 0,
        }}
      />
      {subtitle && (
        <Text
          variant="subtitle"
          component={animate ? motion.p : 'p'}
          color={subtitleColor}
          initial={animate ? { opacity: 0, y: 16 } : undefined}
          whileInView={animate ? { opacity: 1, y: 0 } : undefined}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          sx={{
            maxWidth: 680,
            mx: align === 'center' ? 'auto' : 0,
            display: 'block',
          }}
        >
          {subtitle}
        </Text>
      )}
    </Box>
  );

  return animate ? <ScrollReveal>{content}</ScrollReveal> : content;
};

export default SectionHeader;
